import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Checkbox from "../form/input/Checkbox";
import { userService } from "../../services/userService";

// Importar a constante da mesma forma que foi definida em VerifyEmail.tsx
const PENDING_VERIFICATION_EMAIL_KEY = "pending_verification_email";

export default function SignUpForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    terms: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpa erro do campo quando o usuário começa a digitar
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    // Limpa erro da API quando o usuário faz alguma alteração
    if (apiError) {
      setApiError("");
    }
  };

  const validate = () => {
    const newErrors = {
      name: "",
      email: "",
      password: "",
      terms: ""
    };
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email inválido";
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = "Senha é obrigatória";
      isValid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = "A senha deve ter pelo menos 6 caracteres";
      isValid = false;
    }

    if (!isChecked) {
      newErrors.terms = "Você precisa aceitar os termos e condições";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    setApiError("");

    try {
      await userService.createUser({
        name: formData.name,
        email: formData.email,
        password: formData.password
      });

      // Salvar o e-mail no localStorage para recuperação posterior
      localStorage.setItem(PENDING_VERIFICATION_EMAIL_KEY, formData.email);
      
      // Redirecionar para a página de verificação de e-mail
      // Passamos o e-mail como state para permitir a funcionalidade de reenvio
      navigate("/verify-email", {
        state: { email: formData.email }
      });
    } catch (error: any) {
      console.error("Erro ao cadastrar:", error);

      if (error.response && error.response.data && error.response.data.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError("Ocorreu um erro ao criar sua conta. Por favor, tente novamente.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar">
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          <div className="mb-5 sm:mb-8">
            <img
              src="/images/logo/logo-icon.svg"
              alt="Logo"
              className="mx-auto"
              width={48}
              height={48}
            />
            <h1 className="mt-6 mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              Cadastro
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Insira suas informações para se registrar e começar a vender
            </p>
          </div>
          <div>
            {apiError && (
              <div className="p-3 mb-5 text-sm text-white rounded-lg bg-error-500">
                {apiError}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                {/* <!-- Name --> */}
                <div className="sm:col-span-1">
                  <Label>
                    Nome<span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Digite seu nome completo"
                    className={errors.name ? "border-error-500" : ""}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-error-500">{errors.name}</p>
                  )}
                </div>
                {/* <!-- Email --> */}
                <div>
                  <Label>
                    Email<span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Digite seu melhor email"
                    className={errors.email ? "border-error-500" : ""}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-error-500">{errors.email}</p>
                  )}
                </div>
                {/* <!-- Password --> */}
                <div>
                  <Label>
                    Senha<span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Digite uma senha"
                      type={showPassword ? "text" : "password"}
                      className={errors.password ? "border-error-500" : ""}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      )}
                    </span>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-error-500">{errors.password}</p>
                  )}
                </div>
                {/* <!-- Checkbox --> */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    className="w-5 h-5 mt-1"
                    checked={isChecked}
                    onChange={setIsChecked}
                  />
                  <div>
                    <p className="inline-block text-sm font-normal text-gray-500 dark:text-gray-400">
                      Ao criar uma conta, você concorda com os{" "}
                      <span className="text-gray-800 dark:text-white/90">
                        Termos & Condições,
                      </span>{" "}
                      e com nossas{" "}
                      <span className="text-gray-800 dark:text-white">
                        Políticas de Privacidade
                      </span>
                    </p>
                    {errors.terms && (
                      <p className="mt-1 text-sm text-error-500">{errors.terms}</p>
                    )}
                  </div>
                </div>
                {/* <!-- Button --> */}
                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Processando..." : "Cadastre-se agora"}
                  </button>
                </div>
              </div>
            </form>

            <div className="mt-5">
              <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400">
                Já tem uma conta? {""}
                <Link
                  to="/signin"
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                >
                  Fazer login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
